import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import api from '../services/api';
import { Feedback, ReverseFeedback } from '../types';

const PAGE_SIZE = 3;

interface FeedbackDetailResponse {
  managerToDeveloper: Feedback[];
  developerToManager: ReverseFeedback[];
  managerTotal: number;
  reverseTotal: number;
  managerHasMore: boolean;
  reverseHasMore: boolean;
}

const formatDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleDateString('pt-BR', { dateStyle: 'short' }) : '—';

const formatAdherence = (value?: string): string => {
  if (!value) return '—';
  if (value === 'sim') return 'Sim';
  if (value === 'parcialmente') return 'Parcialmente';
  return 'Não';
};

const formatScore = (v: string | number | undefined): string => {
  if (v === undefined || v === null) return '—';
  const n = typeof v === 'number' ? v : Number.parseFloat(String(v));
  return Number.isFinite(n) ? n.toFixed(2) : String(v);
};

interface Props {
  developerId: string;
}

const ReportsIndividualFeedbackHistory: React.FC<Props> = ({ developerId }) => {
  const [managerFeedbacks, setManagerFeedbacks] = useState<Feedback[]>([]);
  const [reverseFeedbacks, setReverseFeedbacks] = useState<ReverseFeedback[]>([]);
  const [managerHasMore, setManagerHasMore] = useState(false);
  const [reverseHasMore, setReverseHasMore] = useState(false);
  const [managerTotal, setManagerTotal] = useState(0);
  const [reverseTotal, setReverseTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSlice = useCallback(
    async (managerOffset: number, reverseOffset: number, append: boolean): Promise<void> => {
      const isFirst = !append;
      if (isFirst) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const { data } = await api.get<FeedbackDetailResponse>(
          `/reports/individual/${developerId}/feedback-detail`,
          {
            params: {
              limit: PAGE_SIZE,
              managerOffset,
              reverseOffset,
            },
          },
        );
        if (append) {
          setManagerFeedbacks((prev) => [...prev, ...data.managerToDeveloper]);
          setReverseFeedbacks((prev) => [...prev, ...data.developerToManager]);
        } else {
          setManagerFeedbacks(data.managerToDeveloper);
          setReverseFeedbacks(data.developerToManager);
        }
        setManagerHasMore(data.managerHasMore);
        setReverseHasMore(data.reverseHasMore);
        setManagerTotal(data.managerTotal);
        setReverseTotal(data.reverseTotal);
      } catch (e) {
        console.error(e);
        setError('Não foi possível carregar o histórico de feedbacks.');
        if (!append) {
          setManagerFeedbacks([]);
          setReverseFeedbacks([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [developerId],
  );

  useEffect(() => {
    if (!developerId) return;
    void fetchSlice(0, 0, false);
  }, [developerId, fetchSlice]);

  const handleLoadMore = (): void => {
    void fetchSlice(managerFeedbacks.length, reverseFeedbacks.length, true);
  };

  const canLoadMore = managerHasMore || reverseHasMore;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Histórico de feedbacks na tela
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Últimos registros: gestor → desenvolvedor e desenvolvedor → gestor (até {PAGE_SIZE} de cada vez).
          Use o botão abaixo para trazer anteriores.
        </Typography>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {loading ? <Typography color="text.secondary">Carregando histórico…</Typography> : null}

      {!loading && !error ? (
        <>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }} component="section" aria-labelledby="reports-history-m2d">
              <Typography
                id="reports-history-m2d"
                variant="subtitle1"
                component="h4"
                sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
              >
                Gestor → desenvolvedor
                <Typography variant="caption" component="span" color="text.secondary">
                  {managerFeedbacks.length}
                  {managerTotal > 0 ? ` / ${managerTotal}` : ''}
                </Typography>
              </Typography>
              {managerFeedbacks.length === 0 ? (
                <Typography color="text.secondary">Nenhum feedback neste sentido.</Typography>
              ) : (
                <Stack spacing={2}>
                  {managerFeedbacks.map((f) => (
                    <Paper key={f.id} variant="outlined" sx={{ p: 2 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ justifyContent: 'space-between', flexWrap: 'wrap', mb: 1 }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {formatDate(f.createdAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {f.cycle?.name ?? 'Ciclo'} · {f.manager?.name ?? 'Gestor'}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Score final:</strong> {formatScore(f.finalScore)}
                        {' · '}
                        <strong>Produtividade:</strong> {f.productivityScore}
                        {' · '}
                        <strong>Qualidade:</strong> {f.codeQualityScore}
                        {' · '}
                        <strong>Validação:</strong> {f.validationScore}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Aderência código:</strong> {formatAdherence(f.codeAdherence)}
                        {' · '}
                        <strong>Validação:</strong> {formatAdherence(f.validationCompleteness)}
                      </Typography>
                      {f.productivityJustification ? (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Produtividade:</strong> {f.productivityJustification}
                        </Typography>
                      ) : null}
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Pontos fortes:</strong> {f.strengths}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Melhorias:</strong> {f.improvements}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Próximas ações:</strong> {f.nextActions}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} component="section" aria-labelledby="reports-history-d2m">
              <Typography
                id="reports-history-d2m"
                variant="subtitle1"
                component="h4"
                sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
              >
                Desenvolvedor → gestor
                <Typography variant="caption" component="span" color="text.secondary">
                  {reverseFeedbacks.length}
                  {reverseTotal > 0 ? ` / ${reverseTotal}` : ''}
                </Typography>
              </Typography>
              {reverseFeedbacks.length === 0 ? (
                <Typography color="text.secondary">Nenhum feedback reverso registrado.</Typography>
              ) : (
                <Stack spacing={2}>
                  {reverseFeedbacks.map((f) => (
                    <Paper key={f.id} variant="outlined" sx={{ p: 2 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ justifyContent: 'space-between', flexWrap: 'wrap', mb: 1 }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {formatDate(f.createdAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {f.cycle?.name ?? 'Ciclo'} · {f.manager?.name ?? 'Gestor'}
                          {f.anonymous ? ' · anônimo' : ''}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Score final:</strong> {formatScore(f.finalScore)}
                        {' · '}
                        <strong>Clareza:</strong> {f.clarityScore}
                        {' · '}
                        <strong>Disponibilidade:</strong> {f.availabilityScore}
                        {' · '}
                        <strong>Qualidade do feedback:</strong> {f.feedbackQualityScore}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Clareza:</strong> {formatAdherence(f.clarityQuestion)}
                        {' · '}
                        <strong>Disponibilidade:</strong> {formatAdherence(f.availabilityQuestion)}
                        {' · '}
                        <strong>Feedback:</strong> {formatAdherence(f.feedbackQualityQuestion)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>O que o gestor faz bem:</strong> {f.whatManagerDoesWell}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>O que pode melhorar:</strong> {f.whatCanImprove}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Sugestões:</strong> {f.suggestions}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Grid>
          </Grid>

          {canLoadMore ? (
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', mt: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => handleLoadMore()}
                disabled={loadingMore}
              >
                {loadingMore ? 'Carregando…' : 'Carregar feedbacks anteriores'}
              </Button>
            </Stack>
          ) : null}
        </>
      ) : null}
    </Box>
  );
};

export default ReportsIndividualFeedbackHistory;
